import React, { useEffect, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';

import ArrowBackIcon from '@material-ui/icons/ArrowBackIos';
import ArrowNextIcon from '@material-ui/icons/ArrowForwardIos';

import ViewArticleSkeleton from 'app/components/loaders/ViewArticleSkeleton';
import CommentContainer from 'app/components/comments/CommentContainer';

import { paper } from 'app/styles/core'
import { container } from 'app/styles/new-layout'

import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3),

        [theme.breakpoints.down('sm')]: {
            padding: 0,
        }
    },
    paper: {
        ...paper(theme),
        position: 'relative',
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    message: {
        fontSize: '1.2rem',
        lineHeight: 1.7,

        '& p:first-child': {
            marginTop: 0
        }
    },
    fullHeightButton: {
        height: '100%',
        padding: theme.spacing(3),
        justifyContent: 'flex-start',

        [theme.breakpoints.down('sm')]: {
            padding: 0,
        }
    },
    alignLeft: {
        textAlign: 'left'
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    }
}));

export default function ViewArticle(props) {

    const api = useApi();
    const classes = useStyles();
    const params = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [ article, setArticle ] = useState(null);

    const [ nextArticle, setNextArticle ] = useState(null);
    const [ prevArticle, setPrevArticle ] = useState(null);

    const [ loading, setLoading ] = useState(true);

    const getArticle = () => {

        setLoading(true);

        api.get(`/article/${params.id}`)
            .then(res => {
                setArticle(res.article);
                setPrevArticle(res.prevArticle);
                setNextArticle(res.nextArticle);
            })
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        getArticle();
        setArticle(null);
        setNextArticle(null);
        setPrevArticle(null);
    }, [params.id]);

    return (
        <div className={classes.container}>
            <Paper className={classes.paper}>
                { loading &&
                    <ViewArticleSkeleton isMobile={isMobile} />
                }
                { article &&
                    <Grid
                        container
                        spacing={3}
                        wrap={isMobile ? 'wrap' : 'nowrap'}
                        className={classes.marginBottom}
                        >
                        <Grid item sm={12} md={4}>
                            <Typography variant="h3" component="h1" gutterBottom>
                                {article.title}
                            </Typography>
                            <Typography
                                color="secondary"
                                variant="h6"
                                component="div"
                                >
                                <span>
                                    {DateTime.fromISO(article.published_at).toLocaleString(DateTime.DATE_MED)}
                                </span>
                            </Typography>
                        </Grid>
                        <Grid item xs={'auto'}>
                            <Divider orientation="vertical"/>
                        </Grid>
                        <Grid item sm={12} md={8}>
                            <Typography
                                variant='body1'
                                color='textSecondary'
                                className={classes.message}
                                dangerouslySetInnerHTML={{__html: article.message}}
                            />
                        </Grid>
                    </Grid>
                }
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        {prevArticle &&
                            <Button
                                fullWidth
                                startIcon={isMobile ? null : <ArrowBackIcon/>}
                                className={classes.fullHeightButton}
                                component={RouterLink}
                                to={`/articles/${prevArticle.id}`}
                                >
                                <div className={classes.alignLeft}>
                                    <Typography variant='subtitle2' color="textSecondary" gutterBottom>
                                        Previous Article
                                    </Typography>
                                    <Typography variant='h6'>
                                        {prevArticle.title}
                                    </Typography>
                                </div>
                            </Button>
                        }
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        {nextArticle &&
                            <Button
                                fullWidth
                                endIcon={isMobile ? null : <ArrowNextIcon/>}
                                className={classes.fullHeightButton}
                                to={`/articles/${nextArticle.id}`}
                                component={RouterLink}
                                >
                                <div className={classes.alignLeft}>
                                    <Typography variant='subtitle2' color="textSecondary" gutterBottom>
                                        Next Article
                                    </Typography>
                                    <Typography variant='h6'>
                                        {nextArticle.title}
                                    </Typography>
                                </div>
                            </Button>
                        }
                    </Grid>
                </Grid>
            </Paper>
            {article && article.commentable &&
                <Paper className={classes.paper}>
                    <Typography variant='h6' component="h2" gutterBottom>
                        Comments ({article.comments_count})
                    </Typography>
                    <Divider />
                    <CommentContainer
                        commentableType="articles"
                        commentableId={article.id}
                        count={article.comments_count}
                    />
                </Paper>
            }
        </div>
    )
}
